import { ApiResponse } from "../models/api-response";
import { InterviewResponse } from "../models/interview";
import { callApi } from "./api.service";
import {
  StartInterviewResponse,
  EndInterviewResponse,
  InterviewQuestionsResponse,
  QuestionItem,
  InterviewLinkDetail,
  ExtendExpirationResponse,
  InterviewDurationResponse,
  InterviewResumeStateResponse,
  PsychometricSectionsResponse,
  SubmitPsychometricAnswerResponse,
} from "../types/interview.types";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

export const getInterviewByToken = async (
  invitationToken: string
): Promise<ApiResponse<InterviewResponse>> => {
  return callApi<InterviewResponse>(`${apiServerUrl}/api/interviews/${invitationToken}`, {
    method: "GET",
    headers: {
      "content-type": "application/json",
    },
  });
};

export const prepareForInterview = async (
  invitationToken: string
): Promise<ApiResponse<StartInterviewResponse>> => {
  return callApi<StartInterviewResponse>(`${apiServerUrl}/api/interviews/${invitationToken}/start`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
  });
};

/**
 * Manually start an interview after the candidate has set up their media
 * This is separate from the initial navigation/setup
 */
export const startInterview = async (
  interviewId: string
): Promise<ApiResponse<StartInterviewResponse>> => {
  return callApi<StartInterviewResponse>(`${apiServerUrl}/api/interviews/${interviewId}/start-session`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
  });
};

/**
 * Recover the state needed to resume an in-progress interview after a
 * hard reload (browser closed, network drop, refresh). Returns the
 * status, time remaining, invitation token, and a few labels so the
 * page can rebuild its in-memory state without redirecting through
 * the prep page.
 */
export const getInterviewResumeState = async (
  interviewId: string
): Promise<ApiResponse<InterviewResumeStateResponse>> => {
  return callApi<InterviewResumeStateResponse>(
    `${apiServerUrl}/api/interviews/${interviewId}/resume-state`,
    {
      method: "GET",
      headers: { "content-type": "application/json" },
    }
  );
};

export const endInterview = async (
  interviewId: string
): Promise<ApiResponse<EndInterviewResponse>> => {
  return callApi<EndInterviewResponse>(`${apiServerUrl}/api/interviews/${interviewId}/end`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
  });
};

export const getInterviewQuestions = async (
  interviewId: string
): Promise<ApiResponse<InterviewQuestionsResponse>> => {
  try {
    const response = await callApi<InterviewQuestionsResponse>(`${apiServerUrl}/api/interviews/${interviewId}/questions`, {
      method: "GET",
      headers: {
        "content-type": "application/json",
      },
    });
    
    return response;
  } catch (error) {
    console.error(`[interview.service] Error fetching questions:`, error);
    return {
      error: `Failed to fetch questions: ${error}`,
      data: undefined
    };
  }
};

export const getQuestionAudioUrl = (interviewId: string, audioFilename: string): string => {
  const filename = audioFilename.split('/').pop();
  return `${apiServerUrl}/api/interviews/${interviewId}/audio/${filename}`;
};

/**
 * Logs the transition to a new question in the backend.
 */
export const logQuestionTransition = async (
  interviewId: string,
  question: QuestionItem
): Promise<ApiResponse<Record<string, unknown>>> => {
  
  // Construct the request body
  const requestBody = {
    question_index: question.index,
  };

  try {
    const response = await callApi<Record<string, unknown>>(`${apiServerUrl}/api/interviews/${interviewId}/question_transitions`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.error) {
      console.error(`[interview.service] Failed to log question transition:`, response.error);
    }
    
    return response;
  } catch (error) {
    console.error(`[interview.service] Network error logging question transition:`, error);
    return {
      error: `Network error: ${error}`,
      data: undefined
    };
  }
};

/**
 * Logs a sentinel transition event marking the end of interview questions.
 * Uses question_index=999 so the backend computes the last real question's
 * time_spent up to this moment (not up to interview completed_at).
 */
export const logInterviewQuestionsEnd = async (
  interviewId: string,
): Promise<ApiResponse<Record<string, unknown>>> => {
  try {
    const response = await callApi<Record<string, unknown>>(`${apiServerUrl}/api/interviews/${interviewId}/question_transitions`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ question_index: 999 }),
    });
    if (response.error) {
      console.error(`[interview.service] Failed to log interview questions end:`, response.error);
    }
    return response;
  } catch (error) {
    console.error(`[interview.service] Network error logging interview questions end:`, error);
    return { error: `Network error: ${error}`, data: undefined };
  }
};

/**
 * Updates the Monaco IDE state in the backend.
 */
export const updateMonacoState = async (
  interviewId: string,
  code: string,
  language: string,
  questionIndex: number
): Promise<ApiResponse<Record<string, unknown>>> => {
  
  const requestBody = {
    code,
    language,
    question_index: questionIndex,
  };

  try {
    const response = await callApi<Record<string, unknown>>(`${apiServerUrl}/api/interviews/${interviewId}/monaco_state`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (response.error) {
      console.error(`[interview.service] Failed to update Monaco state:`, response.error);
    }
    
    return response;
  } catch (error) {
    console.error(`[interview.service] Network error updating Monaco state:`, error);
    return {
      error: `Network error: ${error}`,
      data: undefined
    };
  }
};

// --- Interfaces ---

export interface Interview {
  interview_id: string;
  job_id: string;
  job_title: string;
  candidate_id: string;
  candidate_name: string;
  started_at: number | null;
  completed_at: string | null;
  company_name?: string;
  creator_email?: string;
  feedback_rating?: number | null;
  feedback_comment?: string | null;
  feedback_submitted_at?: string | null;
}

// --- Service Functions ---

/**
 * Fetches all completed interviews.
 * @param accessToken - The user's access token.
 * @returns Promise resolving to an ApiResponse containing the list of completed interviews.
 */
export const getCompletedInterviews = async (
  accessToken: string
): Promise<ApiResponse<Interview[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<Interview[]>(`${apiServerUrl}/api/interviews/all/completed`, config);
};

/**
 * Downloads interview data as a zip file.
 * @param accessToken - The user's access token.
 * @param interviewId - The ID of the interview to download.
 * @returns Promise resolving to a Blob containing the zip file.
 */
export const downloadInterviewData = async (
  accessToken: string,
  interviewId: string
): Promise<Blob> => {
  const response = await fetch(`${apiServerUrl}/api/interviews/${interviewId}/download`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download interview data');
  }

  return response.blob();
};

/**
 * Reloads/regenerates assessment for a completed interview.
 * This deletes the existing assessment and triggers a new assessment generation.
 * @param accessToken - The user's access token.
 * @param interviewId - The ID of the interview to reload assessment for.
 * @returns Promise resolving to an ApiResponse containing the reload status.
 */
export const reloadAssessment = async (
  accessToken: string,
  interviewId: string
): Promise<ApiResponse<{ message: string; interview_id: string; status: string }>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<{ message: string; interview_id: string; status: string }>(
    `${apiServerUrl}/api/interviews/${interviewId}/reload_assessment`,
    config
  );
};

// --- New Type for Selfie Details --- 
export interface InterviewSelfieDetailsResponse {
  selfie_filename: string | null;
  speech_start_seconds: number;
  /** Signed bucket URL (staging/production). Absent or null: load via getInterviewSelfieVideoBlob. */
  selfie_stream_url?: string | null;
}

// --- New Service Function to get Selfie Details ---
export const getInterviewSelfieDetails = async (
  accessToken: string,
  candidateId: string
): Promise<ApiResponse<InterviewSelfieDetailsResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };
  return callApi<InterviewSelfieDetailsResponse>(
    `${apiServerUrl}/api/interviews/${candidateId}/selfie_details`,
    config
  );
};

// --- Fetch Selfie Video as Blob (with authentication) ---
export const getInterviewSelfieVideoBlob = async (
  accessToken: string,
  candidateId: string,
  selfieFilename: string
): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(`${apiServerUrl}/api/interviews/${candidateId}/selfie_video/${selfieFilename}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      return {
        error: `Failed to fetch selfie video: ${response.status} ${response.statusText}`,
        data: undefined
      };
    }

    const blob = await response.blob();
    return {
      error: undefined,
      data: blob
    };
  } catch (error) {
    console.error(`[interview.service] Error fetching selfie video blob:`, error);
    return {
      error: `Network error: ${error}`,
      data: undefined
    };
  }
};

// --- Question (cheating analysis) video ---
export interface MediaStreamUrlResponse {
  /** Signed bucket URL (staging/production). Null: load via getQuestionVideoBlob. */
  stream_url: string | null;
}

export const getQuestionVideoStreamUrl = async (
  accessToken: string,
  interviewId: string,
  questionIndex: number
): Promise<ApiResponse<MediaStreamUrlResponse>> => {
  return callApi<MediaStreamUrlResponse>(
    `${apiServerUrl}/api/interviews/${interviewId}/question_video/${questionIndex}/stream_url`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
};

export const getQuestionVideoBlob = async (
  accessToken: string,
  interviewId: string,
  questionIndex: number
): Promise<ApiResponse<Blob>> => {
  try {
    const response = await fetch(
      `${apiServerUrl}/api/interviews/${interviewId}/question_video/${questionIndex}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!response.ok) {
      return {
        error: `Failed to fetch question video: ${response.status} ${response.statusText}`,
        data: undefined,
      };
    }
    return { error: undefined, data: await response.blob() };
  } catch (error) {
    console.error(`[interview.service] Error fetching question video blob:`, error);
    return { error: `Network error: ${error}`, data: undefined };
  }
};

// --- Helper to get Selfie Video URL (deprecated - use getInterviewSelfieVideoBlob instead) ---
export const getInterviewSelfieVideoUrl = (candidateId: string, selfieFilename: string): string => {
  // The backend endpoint for serving the video is /api/interviews/{candidateId}/selfie_video/{filename}
  // NOTE: This will fail due to authentication requirements. Use getInterviewSelfieVideoBlob instead.
  return `${apiServerUrl}/api/interviews/${candidateId}/selfie_video/${selfieFilename}`;
};

/**
 * Fetches all interview links with their details for admin users.
 * @param accessToken - The user's access token.
 * @returns Promise resolving to an ApiResponse containing the list of interview link details.
 */
export const getAllInterviewLinks = async (
  accessToken: string
): Promise<ApiResponse<InterviewLinkDetail[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<InterviewLinkDetail[]>(
    `${apiServerUrl}/api/interviews/admin/all_interview_links`,
    config
  );
};



/**
 * Extends the expiration date of an interview by the specified number of days.
 * Works for both recruiters (own jobs only) and admins (any job).
 * @param accessToken - The user's access token.
 * @param interviewId - The ID of the interview to extend.
 * @param days - The number of days to extend the expiration by.
 * @returns Promise resolving to an ApiResponse containing the updated expiration details.
 */
export const extendInterviewExpiration = async (
  accessToken: string,
  interviewId: string,
  days: number
): Promise<ApiResponse<ExtendExpirationResponse>> => {
  const config: RequestInit = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ days }),
  };

  return callApi<ExtendExpirationResponse>(
    `${apiServerUrl}/api/interviews/${interviewId}/extend_expiration`,
    config
  );
};

export const getInterviewDuration = async (
  interviewId: string
): Promise<ApiResponse<InterviewDurationResponse>> => {
  try {
    const response = await callApi<InterviewDurationResponse>(`${apiServerUrl}/api/interviews/${interviewId}/duration`, {
      method: "GET",
      headers: {
        "content-type": "application/json",
      },
    });
    
    return response;
  } catch (error) {
    console.error(`[interview.service] Error fetching interview duration:`, error);
    return {
      error: `Failed to fetch interview duration: ${error}`,
      data: undefined
    };
  }
};

// ==================== Psychometric Functions ====================

/**
 * Fetch psychometric sections embedded in the interview's questions_snapshot.
 * Returns empty list if no psychometric sections exist.
 */
export const getPsychometricSections = async (
  interviewId: string
): Promise<ApiResponse<PsychometricSectionsResponse>> => {
  try {
    const response = await callApi<PsychometricSectionsResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/psychometric-sections`,
      {
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error(`[interview.service] Error fetching psychometric sections:`, error);
    return {
      error: `Failed to fetch psychometric sections: ${error}`,
      data: undefined,
    };
  }
};

/**
 * Submit a single per-question psychometric answer.
 * Answers are stored server-side per test_slug → question_id → answer_value.
 */
export const submitPsychometricAnswer = async (
  interviewId: string,
  testSlug: string,
  questionId: number,
  answerValue: number
): Promise<ApiResponse<SubmitPsychometricAnswerResponse>> => {
  try {
    const response = await callApi<SubmitPsychometricAnswerResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/psychometric-answer`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          test_slug: testSlug,
          question_id: questionId,
          answer_value: answerValue,
        }),
      }
    );
    return response;
  } catch (error) {
    console.error(`[interview.service] Error submitting psychometric answer:`, error);
    return {
      error: `Failed to submit psychometric answer: ${error}`,
      data: undefined,
    };
  }
};

export interface BulkPsychometricAnswersResponse {
  success: boolean;
  saved_count: number;
}

export const bulkSubmitPsychometricAnswers = async (
  interviewId: string,
  answers: Record<string, Record<number, number>>
): Promise<ApiResponse<BulkPsychometricAnswersResponse>> => {
  try {
    // Convert numeric keys to string keys for JSON serialization
    const serialized: Record<string, Record<string, number>> = {};
    for (const [slug, qidMap] of Object.entries(answers)) {
      serialized[slug] = {};
      for (const [qid, val] of Object.entries(qidMap)) {
        serialized[slug][String(qid)] = val;
      }
    }
    return callApi<BulkPsychometricAnswersResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/psychometric-answers-bulk`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ answers: serialized }),
      }
    );
  } catch (error) {
    console.error(`[interview.service] Error bulk submitting psychometric answers:`, error);
    return { error: `Failed to bulk submit psychometric answers: ${error}`, data: undefined };
  }
};

// ==================== LiveKit Egress Recording Functions ====================

export interface LiveKitTokenResponse {
  interview_id: string;
  room_name: string;
  livekit_url: string;
  livekit_token: string;
}

export interface StartRecordingResponse {
  egress_ids: string[];
  success: boolean;
  message: string;
}

export interface StopRecordingResponse {
  success: boolean;
  message: string;
  on_hold_recordings: string[];
  stopped_recordings: string[];
}

/**
 * Get LiveKit token for joining interview room
 */
export const getLiveKitToken = async (
  interviewId: string
): Promise<ApiResponse<LiveKitTokenResponse>> => {
  try {
    const response = await callApi<LiveKitTokenResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/livekit/token`,
      {
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error(`[interview.service] Error getting LiveKit token:`, error);
    return {
      error: `Failed to get LiveKit token: ${error}`,
      data: undefined
    };
  }
};

/**
 * Start LiveKit Egress recording for a specific question
 */
export const startQuestionRecording = async (
  interviewId: string,
  questionId: string
): Promise<ApiResponse<StartRecordingResponse>> => {
  try {
    const response = await callApi<StartRecordingResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/livekit/start-recording?question_id=${questionId}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error(`[interview.service] Error starting question recording:`, error);
    return {
      error: `Failed to start recording: ${error}`,
      data: undefined
    };
  }
};

/**
 * Stop LiveKit Egress recording for current question
 */
export const stopQuestionRecording = async (
  interviewId: string
): Promise<ApiResponse<StopRecordingResponse>> => {
  try {
    const response = await callApi<StopRecordingResponse>(
      `${apiServerUrl}/api/interviews/${interviewId}/livekit/stop-recording`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error(`[interview.service] Error stopping question recording:`, error);
    return {
      error: `Failed to stop recording: ${error}`,
      data: undefined
    };
  }
};